/* iter-panel-inbox — Per-panel email button.
 *
 * Every admin sub-panel is scope-tagged (see `TAB_SCOPES` in
 * `adminTabGroups.js`). This button lives in the admin header and
 * opens a drawer showing the inbox scoped to whatever the
 * currently-viewed tab requires. All admins holding that scope see
 * the SAME rows — the collection is scope-tagged, not per-user, so
 * two bookkeepers reading `financials` mail get the same list and
 * a "read" stamp from either one marks the message read for both.
 */
import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { Mail, Loader2, X, CornerDownRight, CheckCircle2, Send as SendIcon, Download, Settings2, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';
import { TAB_SCOPES } from '@/pages/admin/adminTabGroups';

// Pick the *narrowest* scope the tab requires so the panel email
// button always maps to the most specific mailbox. `pro_writer` is
// dropped from consideration because it's the grand-fathered full-
// admin — anything else in the list is the panel-specific scope.
export function scopeForTab(tabValue) {
  const list = (TAB_SCOPES[tabValue] || []).filter(s => s !== 'pro_writer');
  return list[0] || null;
}

// iter-router-rules-admin-ui — inline CRUD for the local-part → scope
// map. Rendered inside the Panel Inbox drawer under a collapsible
// header so ops actually gets a form to edit rules (instead of curl).
function RouterRulesPanel({ visible }) {
  const [rules, setRules] = React.useState(null);
  const [busy, setBusy]   = React.useState(false);
  const [local, setLocal] = React.useState('');
  const [scopesStr, setScopesStr] = React.useState('financials');
  const [panelHint, setPanelHint] = React.useState('');

  const load = async () => {
    try { setRules(await adminApi.routerRules.list()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load router rules.'); }
  };
  React.useEffect(() => { if (visible) load(); /* eslint-disable-line */ }, [visible]);

  const upsert = async () => {
    const scopes = scopesStr.split(',').map(s => s.trim()).filter(Boolean);
    if (!local.trim() || !scopes.length) { toast.error('Local part + at least one scope required.'); return; }
    setBusy(true);
    try {
      await adminApi.routerRules.upsert({ local: local.trim(), scopes, panel_hint: panelHint.trim() || null });
      toast.success(`Rule saved for '${local.trim()}'.`);
      setLocal(''); setPanelHint(''); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Delete DB rule for '${loc}'? Built-in fallback will resume.`)) return;
    try {
      await adminApi.routerRules.remove(loc);
      toast.success(`Deleted rule '${loc}'.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed.'); }
  };

  if (!visible) return null;
  return (
    <div className="mb-3 rounded-md border border-border bg-card p-2" data-testid="admin-router-rules-panel">
      <div className="mb-2 flex items-center gap-1.5 text-[11.5px] font-semibold">
        <Settings2 className="h-3 w-3 text-primary" /> Router Rules (local → scope)
      </div>
      <div className="mb-2 grid grid-cols-[1fr_1fr_1fr_auto] gap-1.5">
        <Input value={local} onChange={e => setLocal(e.target.value)} placeholder="local (e.g. payouts-eu)" className="h-7 text-[11px]" data-testid="admin-router-rules-local-input" />
        <Input value={scopesStr} onChange={e => setScopesStr(e.target.value)} placeholder="scopes (comma-sep)" className="h-7 text-[11px]" data-testid="admin-router-rules-scopes-input" />
        <Input value={panelHint} onChange={e => setPanelHint(e.target.value)} placeholder="panel_hint (optional)" className="h-7 text-[11px]" data-testid="admin-router-rules-panel-hint-input" />
        <Button size="sm" onClick={upsert} disabled={busy} className="h-7 gap-1" data-testid="admin-router-rules-save-btn">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Save
        </Button>
      </div>
      {!rules ? (
        <div className="py-2 text-center text-[11px] text-muted-foreground"><Loader2 className="mx-auto h-3 w-3 animate-spin" /></div>
      ) : (
        <div className="max-h-[220px] overflow-y-auto rounded-sm border border-border" data-testid="admin-router-rules-list">
          {(rules.db_rules || []).length === 0 && (
            <div className="px-2 py-1.5 text-[11px] italic text-muted-foreground">No DB overrides — built-in rules below are active.</div>
          )}
          {(rules.db_rules || []).map(r => (
            <div key={`db-${r.local}`} className="flex items-center gap-1.5 border-b border-border px-2 py-1 text-[11px]" data-testid={`admin-router-rules-row-db-${r.local}`}>
              <span className="rounded-sm bg-primary/15 px-1 text-[9.5px] font-semibold uppercase text-primary">DB</span>
              <code className="font-mono-stat">{r.local}@</code>
              <span className="text-muted-foreground">→</span>
              <span>{(r.scopes || []).join(', ')}</span>
              {r.panel_hint && <span className="ml-1 text-[10px] text-muted-foreground">· {r.panel_hint}</span>}
              <button type="button" onClick={() => remove(r.local)} className="ml-auto text-muted-foreground hover:text-rose-600" data-testid={`admin-router-rules-delete-${r.local}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          {(rules.builtin || []).map(r => (
            <div key={`bi-${r.local}`} className="flex items-center gap-1.5 border-b border-border/60 px-2 py-1 text-[10.5px] text-muted-foreground" data-testid={`admin-router-rules-row-code-${r.local}`}>
              <span className="rounded-sm bg-muted px-1 text-[9.5px] font-semibold uppercase">CODE</span>
              <code className="font-mono-stat">{r.local}@</code>
              <span>→</span>
              <span>{(r.scopes || []).join(', ')}</span>
              {r.panel_hint && <span className="ml-1 text-[10px]">· {r.panel_hint}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// iter-digest-pdf-branding — HTML wrapper for the print-optimised
// digest window. Includes Crown & Rose seal + signature-line footer
// so the saved PDF looks archival-grade for accountants. Kept as a
// pure string so it can be inlined into the printed document without
// depending on any bundle-time asset.
function brandedDigestHtml(inner, scope) {
  const nowIso  = new Date().toISOString().slice(0, 10);
  const nowFull = new Date().toLocaleString();
  return `<!doctype html><html><head>
<title>Panel Inbox Digest — ${scope || 'admin'} — ${nowIso}</title>
<style>
@page{size:letter;margin:0.6in}
body{font-family:Georgia,'Times New Roman',serif;color:#2b1a12;max-width:640px;margin:24px auto;padding:0 24px;background:#fbf6ef}
.cnr-seal{display:flex;align-items:center;gap:12px;border-bottom:2px solid #7a1e1e;padding-bottom:12px;margin-bottom:18px}
.cnr-seal svg{width:52px;height:52px;flex-shrink:0}
.cnr-seal .title{font-family:Georgia,serif;font-size:22px;color:#7a1e1e;font-variant:small-caps;letter-spacing:1px}
.cnr-seal .sub{font-size:11px;color:#5a4a3c;font-style:italic;margin-top:2px}
.cnr-body{font-size:13px;line-height:1.55}
.cnr-body h2{color:#7a1e1e;font-family:Georgia,serif;font-size:17px;border-bottom:1px solid #d0b48f;padding-bottom:4px}
.cnr-footer{margin-top:36px;border-top:2px solid #7a1e1e;padding-top:14px;font-size:11px;color:#5a4a3c}
.cnr-footer .sig-row{display:flex;gap:24px;margin-top:24px}
.cnr-footer .sig-col{flex:1}
.cnr-footer .sig-line{border-bottom:1px solid #2b1a12;margin-top:32px;padding-top:4px;font-size:10px;color:#5a4a3c}
.cnr-meta{font-size:10px;color:#7a6a5c;font-style:italic;margin-top:8px}
@media print{body{background:#fff} .no-print{display:none}}
</style>
</head><body>
<header class="cnr-seal">
  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <radialGradient id="ros" cx="50%" cy="45%" r="55%">
        <stop offset="0%" stop-color="#c65454"/>
        <stop offset="100%" stop-color="#7a1e1e"/>
      </radialGradient>
    </defs>
    <circle cx="50" cy="50" r="46" fill="none" stroke="#7a1e1e" stroke-width="2"/>
    <path d="M32 22 L50 14 L68 22 L72 34 L60 40 L50 34 L40 40 L28 34 Z" fill="#c9a24a" stroke="#7a1e1e" stroke-width="1.5"/>
    <circle cx="50" cy="60" r="16" fill="url(#ros)" stroke="#5c1414" stroke-width="1"/>
    <path d="M50 48 C56 52 56 60 50 64 C44 60 44 52 50 48 Z" fill="#f0c9c9" opacity="0.65"/>
    <text x="50" y="86" text-anchor="middle" font-family="Georgia,serif" font-size="7" font-style="italic" fill="#5a4a3c">CROWN &amp; ROSE</text>
  </svg>
  <div>
    <div class="title">Crown &amp; Rose</div>
    <div class="sub">Panel Inbox Digest · ${scope ? scope.toUpperCase() : 'ADMIN'} scope · ${nowFull}</div>
  </div>
</header>
<div class="cnr-body">${inner}</div>
<footer class="cnr-footer">
  <div>This digest was generated from the Crown &amp; Rose Panel Inbox on ${nowFull}. Retain for compliance and audit records.</div>
  <div class="sig-row">
    <div class="sig-col">
      <div class="sig-line">Reviewed by (signature)</div>
    </div>
    <div class="sig-col">
      <div class="sig-line">Date</div>
    </div>
  </div>
  <div class="cnr-meta">Document reference: CNR-PID-${nowIso}-${scope || 'x'}</div>
</footer>
<script>window.onload=()=>{setTimeout(()=>window.print(),200)};</script>
</body></html>`;
}

export default function PanelEmailButton({ tabValue, callerScopes = [], fixedScope, label, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  // iter-digest-preview-inline — HTML preview rendered inside the
  // drawer instead of window.open() so ad-blockers don't swallow it.
  const [digestPreview, setDigestPreview] = useState(null);

  // iter-panel-inbox — Scope resolution with an optional `fixedScope`
  // override so a dedicated mailbox button (e.g. dev@) can be mounted
  // in the admin header regardless of the currently-open tab.
  const scope = useMemo(
    () => fixedScope || scopeForTab(tabValue),
    [tabValue, fixedScope],
  );
  const isFull = callerScopes.includes('pro_writer');
  const canSee = isFull || (scope && callerScopes.includes(scope));
  const [rulesOpen, setRulesOpen] = useState(false);

  const load = async () => {
    if (!scope) return;
    setLoading(true);
    try {
      const r = await adminApi.panelInbox.list(scope, tabValue);
      setMessages(r?.messages || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load panel mail.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-line */ }, [open, scope, tabValue]);

  const markRead = async (m) => {
    try {
      await adminApi.panelInbox.markRead(m.id);
      setMessages(prev => prev.map(x => x.id === m.id
        ? { ...x, read_by: Array.from(new Set([...(x.read_by || []), 'you'])) }
        : x));
    } catch (e) { /* noop */ }
  };

  const send = async (m) => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const r = await adminApi.panelInbox.reply(m.id, reply);
      const entry = r?.reply;
      setMessages(prev => prev.map(x => x.id === m.id
        ? { ...x, replies: [...(x.replies || []), entry] }
        : x));
      setReply('');
      toast.success('Reply sent to the shared mailbox.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reply failed.');
    } finally { setSending(false); }
  };

  const seedDemo = async () => {
    try {
      const r = await adminApi.panelInbox.seedDemo();
      if ((r?.inserted || []).length) {
        toast.success(`Seeded demo: ${r.inserted.join(', ')}`);
        load();
      } else {
        toast.info('Demo messages already exist for your scopes.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed.');
    }
  };

  const previewDigest = async () => {
    try {
      const r = await adminApi.panelInbox.previewDigest();
      if (r?.total === 0) {
        toast.info('No unread messages in the last 24h — nothing to preview.');
        return;
      }
      const where = r?.sent
        ? `Sent to ${r.delivered_to}`
        : 'Email service not configured — showing inline preview only.';
      toast.success(`Digest preview: ${r.total} unread across ${r.per_scope.length} scope(s). ${where}`);
      setDigestPreview({
        html:      r?.preview_html || '',
        perScope:  r?.per_scope    || [],
        total:     r?.total        || 0,
        sent:      !!r?.sent,
        sentTo:    r?.delivered_to || null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed.');
    }
  };

  if (!canSee) return null;   // no scope match → don't show the button

  const unread = messages.filter(m => !(m.read_by || []).length).length;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex h-7 items-center gap-1.5 rounded-sm border border-border bg-panel-header/40 px-2 text-[11.5px] font-semibold hover:bg-panel-header/70"
        title={`${label || 'Panel inbox'} · ${scope || 'no scope'}`}
        data-testid={testIdPrefix ? `${testIdPrefix}-btn` : 'panel-email-btn'}
      >
        <Mail className="h-3.5 w-3.5" /> {label || 'Panel Inbox'}
        <span className="rounded-sm bg-muted px-1 text-[9.5px] uppercase text-muted-foreground">{scope || '—'}</span>
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[9.5px] font-bold leading-none text-white" data-testid={testIdPrefix ? `${testIdPrefix}-badge` : 'panel-email-badge'}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex" data-testid="panel-email-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="flex h-full w-[520px] max-w-full flex-col border-l border-border bg-background shadow-xl">
            <header className="flex items-center justify-between border-b border-border px-4 py-3">
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  Panel Inbox · {scope || 'unscoped'}
                </div>
                <div className="text-[13px] font-semibold">Shared mail for this panel</div>
              </div>
              <div className="flex items-center gap-1">
                {isFull && (
                  <Button size="sm" variant="ghost" onClick={() => setRulesOpen(v => !v)} data-testid="panel-email-router-rules-toggle">
                    <Settings2 className="h-3.5 w-3.5" /> {rulesOpen ? 'Hide rules' : 'Router rules'}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={previewDigest} data-testid="panel-email-preview-digest">
                  Send me a test digest now
                </Button>
                <Button size="sm" variant="ghost" onClick={seedDemo} data-testid="panel-email-seed-demo">
                  Seed demo
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </header>

            <div className="flex-1 min-h-0 overflow-y-auto scroll-thin p-3">
              <RouterRulesPanel visible={rulesOpen && isFull} />
              {digestPreview && (
                <div className="mb-3 rounded-md border border-primary/40 bg-primary/5 p-2" data-testid="panel-email-digest-preview">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-primary">
                      <SendIcon className="h-3 w-3" />
                      Digest preview · {digestPreview.total} unread across {digestPreview.perScope.length} scope{digestPreview.perScope.length === 1 ? '' : 's'}
                      {digestPreview.sent && (
                        <span className="ml-1 rounded-sm bg-emerald-500/15 px-1.5 text-[9.5px] uppercase text-emerald-700">sent to {digestPreview.sentTo}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => {
                        // iter-digest-preview-pdf + iter-digest-pdf-branding —
                        // opens a print-optimised window with the Crown & Rose
                        // seal + signature-line footer so the saved PDF looks
                        // archival-grade for accountants.
                        const w = window.open('', '_blank');
                        if (!w) { toast.error('Popup blocked — allow popups to print.'); return; }
                        w.document.open();
                        w.document.write(brandedDigestHtml(digestPreview.html, scope));
                        w.document.close();
                      }} data-testid="panel-email-digest-preview-pdf">
                        <Download className="h-3 w-3" /> PDF
                      </Button>
                      <button type="button" onClick={() => setDigestPreview(null)} className="text-muted-foreground hover:text-foreground" data-testid="panel-email-digest-preview-close">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div
                    className="max-h-[280px] overflow-y-auto rounded-sm border border-border bg-background p-2 text-[12px]"
                    // Defense-in-depth: DOMPurify strips any unexpected tags even
                    // though digestPreview.html is backend-generated trusted HTML.
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(digestPreview.html || '') }}
                  />
                </div>
              )}
              {loading ? (
                <div className="py-8 text-center text-[12px] text-muted-foreground">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="py-8 text-center text-[12px] text-muted-foreground" data-testid="panel-email-empty">
                  No mail routed to this panel yet. Use <b>Seed demo</b> to drop a sample message
                  for your scopes, or wait for the inbound router to tag real inbound mail.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="panel-email-list">
                  {messages.map(m => {
                    const isRead = (m.read_by || []).length > 0;
                    const isActive = activeId === m.id;
                    return (
                      <li key={m.id} className={`rounded-md border p-2 ${isRead ? 'border-border' : 'border-primary/60 bg-primary/5'}`} data-testid={`panel-email-row-${m.id}`}>
                        <button
                          type="button"
                          onClick={() => { setActiveId(isActive ? null : m.id); if (!isRead) markRead(m); }}
                          className="flex w-full items-start gap-2 text-left"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 text-[12.5px] font-semibold">
                              {isRead && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                              {m.subject}
                            </div>
                            <div className="mt-0.5 truncate text-[10.5px] text-muted-foreground">
                              from {m.from} · {new Date(m.at).toLocaleString()}
                            </div>
                          </div>
                        </button>
                        {isActive && (
                          <div className="mt-2 border-t border-border pt-2">
                            <p className="whitespace-pre-wrap text-[12px] text-foreground">{m.body_md}</p>
                            {(m.replies || []).map((r, i) => (
                              <div key={i} className="mt-2 flex gap-1 rounded-sm bg-muted/60 p-2 text-[11.5px]">
                                <CornerDownRight className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                                <div>
                                  <div className="text-[10px] text-muted-foreground">{r.by} · {new Date(r.at).toLocaleString()}</div>
                                  <div className="mt-0.5 whitespace-pre-wrap">{r.body_md}</div>
                                </div>
                              </div>
                            ))}
                            <Textarea
                              value={reply}
                              onChange={(e) => setReply(e.target.value)}
                              placeholder="Reply — visible to everyone with this scope."
                              rows={3}
                              className="mt-2 text-[12px]"
                              data-testid={`panel-email-reply-${m.id}`}
                            />
                            <div className="mt-1 flex items-center justify-between">
                              <div className="text-[10.5px] text-muted-foreground">
                                Read by {(m.read_by || []).length} admin{(m.read_by || []).length === 1 ? '' : 's'}
                              </div>
                              <Button size="sm" onClick={() => send(m)} disabled={sending || !reply.trim()} data-testid={`panel-email-send-${m.id}`}>
                                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Send reply'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
